/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { BatchOperationAdd, BatchOperationUpdate, IBlobInfo, IRListItem } from '@rhybus/api'
import { convertToRaw } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import React, { RefObject, forwardRef, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  CREATE_INQUIRY,
  INQUIRIES,
  MY_INQUIRIES,
  PAGE_TITLE,
  PROFILE,
  ROUTE_TYPE,
} from '../../../Configuration'
import { sitePath } from '../../../assets/ts/api/api'
import { mpfRListsHttp } from '../../../assets/ts/api/mpf-r-lists-http'
import { mpfWhoHttp } from '../../../assets/ts/api/mpf-who-http'
import { deleteItem, getBlobStorage, transactionBatch } from '../../../assets/ts/data/data'
import {
  Inquiry,
  LIST_NAME as InquiryList,
  IsForSelf,
  addOrUpdateInquiry,
} from '../../../assets/ts/data/inquiry/inquiry'
import { LIST_NAME as InquiryHistoryList } from '../../../assets/ts/data/inquiry/inquiry-history'
import { STATUS_CODE } from '../../../assets/ts/data/inquiry/inquiry-status-metadata'
import { LIST_NAME as KnowledgeBaseList } from '../../../assets/ts/data/knowledge-base'
import { formatUTCDateToLocal } from '../../../assets/ts/date-and-time/date-and-time'
import { twoDigits } from '../../../assets/ts/formatting/formatting'
import {
  decodeHTML,
  encodeHTML,
  formatBody,
  generateCode,
  getMAJCOMCode,
  getUserProfileWithoutID,
  isChangeDetected,
  listAttachments,
  scrollToTop,
  sendEmail,
} from '../../../assets/ts/general/general'
import { getPIIScanInfo, loadPII } from '../../../assets/ts/pii/pii'
import {
  isDODValid,
  isDropDownValid,
  isEmailValid,
  isInputValid,
  isPhoneNumberValid,
} from '../../../assets/ts/validations/validations'
import Accordions from '../../../components/accordions/accordions'
import Alert from '../../../components/alert/alert'
import Buttons from '../../../components/buttons/buttons'
import CheckListCreateInquiry from '../../../components/checklist-list/checklist-createInquiry'
import InquiryFormValidationMsgs from '../../../components/form-validation-msgs/inquiry-form-validation-msgs'
import Instructions from '../../../components/instructions/instructions'
import PageOverlayBusy from '../../../components/page-overlay/page-overlay-busy'
import SuccessMessage from '../../../components/popups/success-message'
import useInquiry from '../../../hooks/inquiry/use-inquiry'
import useInquiryHistory from '../../../hooks/inquiry/use-inquiry-history'
import useKnowledgeBase from '../../../hooks/use-knowledge-base'
import usePageTitle from '../../../hooks/use-page-title'
import usePascodeMetadata from '../../../hooks/use-pascode-metadata'
import useUsers from '../../../hooks/use-users'
import PageHeader from '../../../layouts/header/page-header'
import { MetadataContext } from '../../../store/metadata-context-provider'
import { PreviousPageContext } from '../../../store/previous-page-provider'
import { RoleContext } from '../../../store/role-context-provider'
import { UserContext } from '../../../store/user-context-provider'
import { INQUIRY_STATUS_IDS } from '../../../utils/constants/global-constants'
import '../create.css'
import CreateActionBtns from '../create-action-btns'
import CustomerDetails from './customer-details'
import InquiryDetails from './inquiry-details'
import RelatedKnowledgeBase from './related-knowledge-base'

interface UserInputs {
  AssignedComponentCode: null | number
  CategoryCode: null | number
  CurrentlyServicingBy: string
  Customer: string
  CustomerStatus: string
  Description: string
  DoDNumber: string
  DutyEmail: string
  DutyPhone: string
  FY: number
  Installation: string
  OrgMAJCOM: string
  Organization: string
  PASCODE: string
  PASCODEServicedBy: string
  PersonalEmail: string
  PersonalPhoneNumber: string
  PersonalPhoneProvider: string
  Rank: string
  SectionCode: null | number
  ServicingBy: string
  ServicingMAJCOMCode: null | number
  ServicingOrganization: string
  StatusCode: string
  SubCategoryCode: null | number
  Subject: string
}
interface BatchResult {
  Errors: any
  result: 'FAILED' | 'PASS'
  results: any
}
const initialPASCODECheck = {
  HasLocation: false,
  HasOrganization: false,
  HasPASCODE: 'Yes',
}
const WHEN_TO_CHECK_PII: 'first' | 'last' = 'last'
// todo: the value for ServicingBy and CurrentlyServicingBy should be
// dynamically determined

const InquiryForm = (props: any, ref: any) => {
  usePageTitle(PAGE_TITLE.CREATE_INQUIRY)
  const closeRef: any = null

  const { guid } = useParams()
  const [refresh, setRefresh] = useState(false)
  const { inquiry, inquiryIsLoading } = useInquiry({ inquiryGuid: guid, refresh: refresh }, !guid)
  const { inquiryHistory } = useInquiryHistory({ inquiryGuid: guid }, !guid)
  const existingInquiry: any = inquiry[0] ?? undefined
  const existingInquiryHistory: any = inquiryHistory[0] ?? undefined

  const { me, userProfile } = useContext(UserContext)
  const { isCPOwner, isCustomer } = useContext(RoleContext)
  const navigate = useNavigate()

  const currentDate = new Date()
  const assignedComponentRef: any = useRef(null)
  const assignedComponentCodeRef: any = useRef(null)
  const categoryCodeRef: RefObject<any> = useRef(null)
  const customerStatusRef: any = useRef(null)
  const descriptionRef: any = useRef(null)
  const dodNumberRef: any = useRef(null)
  const dutyEmailRef: any = useRef(null)
  const dutyPhoneRef: any = useRef(null)
  const installationRef: any = useRef(null)
  const majcomRef: any = useRef(null)
  const organizationRef: any = useRef(null)
  const pascodeRef: any = useRef(null)
  const rankRef: any = useRef(null)
  const sectionCodeRef: any = useRef(null)
  const subjectRef: any = useRef(null)
  const validRef: RefObject<any> = useRef(null)

  const {
    categories,
    checklistsMetadata,
    checklistsMetadataLookup,
    majcoms, // use this metadata to get the list of MAJCOMs
    sections,
    subcategories,
    tooltipObj,
  } = useContext(MetadataContext)

  const currentUserObj = userProfile[0]
  const [checklistCount, setChecklistCount] = useState(0)
  const [knowledgeBaseCount, setKnowledgeBaseCount] = useState<number>(0)

  const [alertMessage, setAlertMessage] = useState<string>('')
  const [selectedUserEmail, setSelectedUserEmail] = useState<string>('')

  const [existingFiles, setExistingFiles] = useState<any[]>([])
  const [files, setFiles] = useState<any[]>([])
  const [filteredKnowledgeBase, setFilteredKnowledgeBase] = useState<any[]>([])
  const [initialKnowledgeBase, setInitialKnowledgeBase] = useState<any[]>([])
  const [selectedChecklist, setSelectedChecklist] = useState([])

  const [activeButton, setActiveButton] = useState<boolean>(true)
  const [displayAlert, setDisplayAlert] = useState<boolean>(false)
  const [displayPII, setDisplayPII] = useState(false)
  const [dodPopulated, setDodPopulated] = useState(false)
  const [dutyMailDisable, setDutyMailDisable] = useState(false)
  const [expandDetailsAccordion, setExpandDetailsAccordion] = useState(false)
  const [fileError, setFileError] = useState(false)
  const [inquiryForSelf, setInquiryForSelf] = useState<IsForSelf>(false)
  const [isInquiryEditing, setIsInquiryEditing] = useState(false)
  const [isValid, setIsValid] = useState(true)
  const [loading, setLoading] = useState(false)
  const [profileLoaded, setProfileLoaded] = useState(false)
  const [show, setShow] = useState(false)
  const [showBottomSubmit, setShowBottomSubmit] = useState(false)
  const [showCloseAlert, setShowCloseAlert] = useState(false)
  const [showCreateNewPopup, setShowCreateNewPopup] = useState(false)
  const [showDiscardPopup, setShowDiscardPopup] = useState(false)
  const [showPageOverlay, setShowPageOverlay] = useState(false)
  const [showNoChanges, setShowNoChanges] = useState(false)
  const [showReadonlyInput, setShowReadonlyInput] = useState(false)
  const [showSaveSuccess, setShowSaveSuccess] = useState(false)
  const [showTopSubmit, setShowTopSubmit] = useState(false)
  const [showUpdatePopup, setShowUpdatePopup] = useState(false)

  const [pascodeChecks, setPascodeChecks] = useState(initialPASCODECheck)
  const [piiScan, setPIIScan] = useState({}) //PII

  const [inquiryType, setInquiryType] = useState('')
  const [actionBtnShowHide, setActionBtnShowHide] = useState({ close: false })
  const { KnowledgeBaseData } = useKnowledgeBase({ isArchived: false })
  const { pascodeMetadata } = usePascodeMetadata({ IsArchived: false })
  const { currentPage, previousPage, setCurrentPage, setPreviousPage } =
    useContext(PreviousPageContext)

  const [selfForOther, setSelfForOther] = useState({
    DoDForOther: { isValid: true },
    DoDNumberSelf: { isValid: true },
    EmailChange: { isValid: true },
    EmailForOther: { isValid: true },
  })

  const [searchedUserId, setSearchedUserId] = useState({})
  const [uniqueChange, setUniqueChange] = useState<string>('')
  const { users } = useUsers({ searchedUserId })

  const initialUserInputs: UserInputs = {
    AssignedComponentCode: 143, //default to 'Regular Air Force'
    CategoryCode: null,
    CurrentlyServicingBy: '',
    Customer: '',
    CustomerStatus: '',
    Description: '',
    DoDNumber: '',
    DutyEmail: '',
    DutyPhone: '',
    FY: currentDate?.getFullYear(),
    Installation: '',
    OrgMAJCOM: '',
    Organization: '',
    PASCODE: '',
    PASCODEServicedBy: '',
    PascodeID: -1,
    PersonalEmail: '',
    PersonalPhoneNumber: '',
    PersonalPhoneProvider: '',
    Rank: '',
    SectionCode: null,
    ServicingBy: '',
    ServicingMAJCOMCode: -1,
    ServicingOrganization: '',
    StatusCode: 'SUBMITTED',
    SubCategoryCode: null,
    Subject: '',
  }
  const [userInputs, setUserInputs] = useState<UserInputs>(initialUserInputs)
  const [existingInquiryDetails, setExistingInquiryDetails] =
    useState<UserInputs>(initialUserInputs)
  // This state is necessary in order to re-render the validation messages on the src/components/form-validation-msgs/inquiry-form-validation-msgs.tsx file
  const [inputValidationObj, setInputValidationObj] = useState({
    AssignedComponent: { isValid: false, link: assignedComponentCodeRef, order: 2 },
    AssignedComponentCode: { isValid: false, link: assignedComponentCodeRef, order: 3 },
    CategoryCode: { isValid: false, link: categoryCodeRef, order: 13 },
    CustomerStatus: { isValid: false, link: customerStatusRef, order: 4 },
    Description: { isValid: false, link: descriptionRef, order: 15 },
    DoDNumber: { isValid: false, link: dodNumberRef, order: 1 },
    DutyEmail: { isValid: false, link: dutyEmailRef, order: 2 },
    DutyPhone: { isValid: false, link: dutyPhoneRef, order: 6 },
    Installation: { isValid: false, link: installationRef, order: 7 },
    Majcom: { isValid: false, link: majcomRef, order: 10 },
    Organization: { isValid: false, link: organizationRef, order: 8 },
    PASCODE: { isValid: false, link: pascodeRef, order: 9 },
    Rank: { isValid: false, link: rankRef, order: 5 },
    SectionCode: { isValid: false, link: sectionCodeRef, order: 14 },
    ServicingMAJCOM: { isValid: false, link: useRef(null), order: 11 },
    Subject: { isValid: false, link: subjectRef, order: 12 },
  })

  async function getFormBlobStorage() {
    const list = await mpfRListsHttp().getList(InquiryList)
    const listBlobProvider = list.blobs()
    const listBlobInfos: IBlobInfo[] = await listBlobProvider.list(
      existingInquiry.eKey as string,
      '/Customer'
    )

    if (listBlobInfos.length > 0) {
      setFiles(listBlobInfos)
      setExistingFiles(listBlobInfos)
    } else {
      setFiles([])
      setExistingFiles(listBlobInfos)
    }
  }

  function buttonReferences(type: any) {
    if (type !== 'close') {
      closeRef?.current?.closeBtn()
    }
  }

  function isMatch(metaData, type, value) {
    return metaData
      .map((item) => {
        return item[type].toUpperCase() === value?.toUpperCase()
      })
      .includes(true)
  }

  /**
   * Handles the validation checks for submitting and saving the inquiry
   * This function is intended to be used before sending any data to the server as a prerequisite in order to display the appropriate validation messages to the user
   * @returns {boolean} a boolean that can then be used to continue/terminate the submission/saving process of the inquiry
   */
  const handleInputValidation = (action): boolean => {
    let inputIsValid = true
    const isSavedAction = action === INQUIRY_STATUS_IDS.Saved
    Object.keys(inputValidationObj).forEach(async (key) => {
      switch (key) {
        case 'DutyEmail':
          inputValidationObj[key].isValid = isEmailValid(userInputs[key])
          if (!inputValidationObj[key].isValid) inputIsValid = false
          break
        case 'DutyPhone':
          inputValidationObj[key].isValid = isPhoneNumberValid(userInputs[key])
          if (!inputValidationObj[key].isValid) inputIsValid = false
          break
        case 'DoDNumber':
          inputValidationObj[key].isValid = isDODValid(userInputs[key])
          if (!inputValidationObj[key].isValid) inputIsValid = false
          break
        case 'Description':
          inputValidationObj[key].isValid = isSavedAction
          || userInputs[key]?.getCurrentContent()?.hasText()
          if (!inputValidationObj[key].isValid && !isSavedAction) inputIsValid = false
          break
        case 'Subject':
          inputValidationObj[key].isValid = isSavedAction || isInputValid(userInputs[key])
          if (!inputValidationObj[key].isValid && !isSavedAction) inputIsValid = false
          break
        case 'CategoryCode':
        case 'SectionCode':
          inputValidationObj[key].isValid = isSavedAction || isDropDownValid(userInputs[key] ?? '')
          if (!inputValidationObj[key].isValid && !isSavedAction) inputIsValid = false
          break
        case 'AssignedComponent':
          inputValidationObj[key].isValid =
            isCPOwner ||
            isCustomer ||
            inquiryForSelf ||
            userInputs?.AssignedComponentCode === currentUserObj?.AssignedComponentCode
          if (
            !inquiryForSelf &&
            !(isCPOwner || isCustomer) &&
            userInputs?.AssignedComponentCode !== currentUserObj?.AssignedComponentCode
          )
            inputIsValid = false
          break
        case 'AssignedComponentCode':
        case 'CustomerStatus':
        case 'Rank':
          inputValidationObj[key].isValid = isDropDownValid(userInputs[key] ?? '')
          if (!inputValidationObj[key].isValid) inputIsValid = false
          break
        case 'PASCODE':
          if (pascodeChecks.HasPASCODE === 'Yes') {
            if (!isMatch(pascodeMetadata, 'PASCODE', userInputs.PASCODE)) {
              inputValidationObj[key].isValid = false
            } else {
              inputValidationObj[key].isValid = true
            }
          } else {
            inputValidationObj[key].isValid = true
          }
          if (!inputValidationObj[key].isValid) inputIsValid = false
          break
        case 'Installation':
          if (pascodeChecks.HasPASCODE === 'No') {
            if (!isMatch(pascodeMetadata, 'Installation', userInputs.Installation)) {
              inputValidationObj[key].isValid = false
            } else {
              inputValidationObj[key].isValid = true
            }
          } else {
            inputValidationObj[key].isValid = true
          }
          if (!inputValidationObj[key].isValid) inputIsValid = false
          break
        case 'Organization':
          if (
            pascodeChecks.HasPASCODE === 'No' &&
            isMatch(pascodeMetadata, 'Installation', userInputs.Installation)
          ) {
            if (!isMatch(pascodeMetadata, 'Organization', userInputs.Organization)) {
              inputValidationObj[key].isValid = false
            } else {
              inputValidationObj[key].isValid = true
            }
          } else {
            inputValidationObj[key].isValid = true
          }
          if (!inputValidationObj[key].isValid) inputIsValid = false
          break
        default:
          break
      }
    })

    setInputValidationObj({ ...inputValidationObj })
    setIsValid(inputIsValid && selfForOther.EmailChange.isValid === true)
    return inputIsValid && selfForOther.EmailChange.isValid === true
  }

  const changeInquiryType = (e) => {
    const isForSelf = e.target.value === 'ForSelf'
    setInquiryForSelf(isForSelf) //sets boolean
    if (!isForSelf) {
      setUserInputs({
        ...userInputs,
        AssignedComponentCode: -1,
        Customer: '',
        CustomerStatus: '',
        DoDNumber: '',
        DutyEmail: '',
        DutyPhone: '',
        Installation: '',
        OrgMAJCOM: '',
        Organization: '',
        PASCODE: '',
        PASCODEServicedBy: '',
        PascodeID: -1,
        Rank: '',
        ServicingMAJCOMCode: -1,
        ServicingOrganization: '',
      })
      setSelectedUserEmail('')
      setPascodeChecks((prevPascodeChecks) => ({
        ...prevPascodeChecks,
        HasLocation: false,
        HasOrganization: false,
        HasPASCODE: 'Yes',
      }))

      setShowReadonlyInput(false)
      setDodPopulated(false)
      setDutyMailDisable(false)
      setSelfForOther({
        ...selfForOther,
        DoDForOther: { isValid: true },
        DoDNumberSelf: { isValid: true },
        EmailChange: { isValid: true },
        EmailForOther: { isValid: true },
      })
    }
  }

  const handleError = (error, contextMessage) => {
    console.error(`${contextMessage}:`, error)
    setAlertMessage(`${contextMessage}: ${error.message}`) //this will be removed last validation is added
    setDisplayAlert(true)
  }

  const userProfileChange = async () => {
    let hasChanged = false
    let isNewProfile = false
    const userProfileData: any =
      (await getUserProfileWithoutID(userInputs.DoDNumber, 'DoDNumber')) || {}
    if (Object.values(userProfileData).length > 0) {
      let oldProfileData: Partial<Inquiry> = {}

      oldProfileData = {
        // ...userInputs,
        AssignedComponentCode: userProfileData.AssignedComponentCode,
        CustomerStatus: userProfileData.CustomerStatus,
        DoDNumber: userProfileData.DoDNumber,
        DutyEmail: userProfileData.DutyEmail,
        DutyPhone: userProfileData.DutyPhone,
        Installation: userProfileData.Installation,
        OrgMAJCOM: userProfileData.OrgMAJCOM,
        Organization: userProfileData.Organization,
        PASCODE: userProfileData.PASCODE,
        PASCODEServicedBy: userProfileData.PASCODEServicedBy,
        PascodeID: userProfileData.PascodeID,
        Rank: userProfileData.Rank,
        ServicingMAJCOMCode: getMAJCOMCode(userProfileData.ServicingMAJCOM, majcoms),
        ServicingOrganization: userProfileData.ServicingOrganization,
      }
      const newProfileData = {
        AssignedComponentCode: userInputs.AssignedComponentCode,
        CustomerStatus: userInputs.CustomerStatus,
        DoDNumber: userInputs.DoDNumber,
        DutyEmail: userInputs.DutyEmail,
        DutyPhone: userInputs.DutyPhone,
        Installation: userInputs.Installation,
        OrgMAJCOM: userInputs.OrgMAJCOM,
        Organization: userInputs.Organization,
        PASCODE: userInputs.PASCODE,
        PASCODEServicedBy: userInputs.PASCODEServicedBy,
        PascodeID: userInputs.PascodeID,
        Rank: userInputs.Rank,
        ServicingMAJCOMCode: userInputs.ServicingMAJCOMCode,
        ServicingOrganization: userInputs.ServicingOrganization,
      }

      Object.keys(newProfileData).forEach((key, i) => {
        if (
          String(newProfileData.DutyEmail.toLowerCase()) !==
          (oldProfileData.DutyEmail ?? '').toLowerCase()
        ) {
          hasChanged = true
        } else if (String(newProfileData[key]) !== String(oldProfileData[key])) {
          hasChanged = true
        }
      })
    }
    if (Object.keys(userProfileData).length === 0) isNewProfile = true

    return { hasChanged, isNewProfile }
  }

  const onSaveAsDraft = async (position: 'bottom' | 'top') => {
    setShowPageOverlay(true)
    saveOrSubmitInquiry('SAVED', position, WHEN_TO_CHECK_PII) //second param is irrelevant for saved inqs
  }

  const handleCreateOrUpdateProfile = async (action, type) => {
    const LIST_NAME = 'UserProfileList'
    setShowUpdatePopup(false)
    setShowCreateNewPopup(false)
    setShowPageOverlay(true)
    setUniqueChange('')

    const newUserData = [...(await mpfWhoHttp().Picker(userInputs.DutyEmail))]
    const customerID = newUserData[0].rid
    const UserDisplayName = newUserData[0].persona

    let userProfileObj: any = {
      AssignedComponentCode: userInputs.AssignedComponentCode,
      DoDNumber: userInputs.DoDNumber,
      DutyEmail: userInputs.DutyEmail,
      DutyPhone: userInputs.DutyPhone,
      IsArchived: false,
      ItemCreated: new Date(),
      ItemCreatedBy: customerID,
      ItemModified: new Date(),
      ItemModifiedBy: customerID,
      PascodeID: userInputs.PascodeID,
      ProfileStatus: userInputs.CustomerStatus,
      Rank: userInputs.Rank,
      UserDisplayName: UserDisplayName,
      UserRID: customerID,
    }

    if (action === 'update') {
      const profileList = await mpfRListsHttp().getList(LIST_NAME)
      const profileItem = await profileList
        .query()
        .select('eKey', 'eTag')
        .filter(`UserRID eq '${customerID}'`)
        .top(1)
        .get()
      userProfileObj = {
        ...userProfileObj,
        eKey: profileItem[0].eKey,
        eTag: profileItem[0].eTag,
      }
    }

    try {
      const profileList = await mpfRListsHttp().getList(LIST_NAME)
      await profileList.addOrUpdateItem(userProfileObj as IRListItem)
    } catch (error) {
      console.error(error)
    }
    setShowPageOverlay(false)
    submitInquiry(type)
  }

  const submitInquiry = async (action?: any) => {
    if (loading) return // Exit if already in the middle of a submission
    let currentlyServicingBy = userInputs.PASCODEServicedBy
    setLoading(true) // Set loading to true to prevent multiple submissions

    if (userInputs.CategoryCode && currentlyServicingBy === 'CSS') {
      if (
        !categories?.filter((cat) => cat.CategoryCode === userInputs.CategoryCode)[0]?.ServicingBy
      ) {
        currentlyServicingBy = 'MPF'
      }
    }
    try {
      const userProfile: any = await mpfWhoHttp().Picker(userInputs.DutyEmail.trim())
      const userId = await currentUserObj.UserRID
      const serverInquiryObject: Partial<Inquiry> = {
        AssignedComponentCode: userInputs.AssignedComponentCode,
        CategoryCode: generateCode(userInputs.CategoryCode as number),
        CurrentlyServicingBy: currentlyServicingBy,
        Customer: userProfile[0].rid,
        CustomerStatus: userInputs.CustomerStatus,
        Description: draftToHtml(convertToRaw(userInputs.Description.getCurrentContent())),
        DoDNumber: encodeHTML(userInputs.DoDNumber),
        DutyEmail: encodeHTML(userInputs.DutyEmail),
        DutyPhone: encodeHTML(userInputs.DutyPhone),
        FY: userInputs.FY,
        InquiryType: inquiryForSelf ? 'Self' : 'Other',
        Installation: encodeHTML(userInputs.Installation),
        IsArchived: false,
        ItemCreated: new Date(),
        ItemCreatedBy: userId,
        ItemModified: new Date(),
        ItemModifiedBy: userId,
        OrgMAJCOM: userInputs.OrgMAJCOM,
        Organization: encodeHTML(userInputs.Organization),
        PASCODE: encodeHTML(userInputs.PASCODE),
        PASCODEServicedBy: userInputs.PASCODEServicedBy,
        PascodeID: generateCode(userInputs.PascodeID),
        PersonalEmail: encodeHTML(userInputs.PersonalEmail),
        PersonalPhoneNumber: encodeHTML(userInputs.PersonalPhoneNumber),
        PersonalPhoneProvider: encodeHTML(userInputs.PersonalPhoneProvider),
        Rank: userInputs.Rank,
        SectionCode: generateCode(userInputs.SectionCode as number),
        ServicingBy: currentlyServicingBy,
        ServicingMAJCOMCode: userInputs.ServicingMAJCOMCode,
        ServicingOrganization: userInputs.ServicingOrganization,
        StatusCode:
          action === INQUIRY_STATUS_IDS.Saved ? INQUIRY_STATUS_IDS.Saved : userInputs.StatusCode,
        SubCategoryCode: generateCode(userInputs.SubCategoryCode as number),
        Subject: encodeHTML(userInputs.Subject),
        eKey: existingInquiry ? existingInquiry.eKey : '',
        eTag: existingInquiry ? existingInquiry.eTag : '',
      }
      if (!existingInquiry) {
        serverInquiryObject.InquiryGUID = crypto.randomUUID()
        serverInquiryObject.InquiryID = crypto.randomUUID()
      } else {
        serverInquiryObject.InquiryGUID = existingInquiry.InquiryGUID
        serverInquiryObject.InquiryID = existingInquiry.InquiryID
      }

      let addedInquiry
      if (existingInquiry) {
        addedInquiry = serverInquiryObject
      } else {
        const initialAddedInquiry = await addOrUpdateInquiry(serverInquiryObject)
        addedInquiry = await addOrUpdateInquiry({
          InquiryID: generateID(userInputs.DoDNumber, initialAddedInquiry.ID),
          eKey: initialAddedInquiry.eKey,
          eTag: initialAddedInquiry.eTag,
        })
      }

      const addHistoryObj = {
        ActionCode: 'SUBMIT',
        AssignedTo: '',
        InquiryGUID: addedInquiry.InquiryGUID,
        InquiryID: addedInquiry.InquiryID,
        IsArchived: false,
        ItemCreated: addedInquiry.ItemCreated,
        ItemCreatedBy: addedInquiry.ItemCreatedBy,
        ItemModified: addedInquiry.ItemModified,
        ItemModifiedBy: addedInquiry.ItemModifiedBy,
        PreviousSectionCode: addedInquiry.PreviousSectionCode,
        PreviousStatusCode: addedInquiry.PreviousStatusCode,
        SectionCode: addedInquiry.SectionCode,
        ServicingBy: addedInquiry.ServicingBy,
        StatusCode: addedInquiry.StatusCode,
        UserRole: !inquiryForSelf ? 'Submitter' : 'Customer',
        eKey: existingInquiryHistory ? existingInquiryHistory.eKey : '',
        eTag: existingInquiryHistory ? existingInquiryHistory.eTag : '',
      }
      // Use the transactionInquiry function to batch add/update inquiry and history
      let batchResult: BatchResult | null
      if (action === 'SAVED') {
        batchResult = await transactionBatch([
          { data: addedInquiry, list: InquiryList, op: BatchOperationUpdate },
        ])
      } else {
        batchResult = await transactionBatch([
          { data: addedInquiry, list: InquiryList, op: BatchOperationUpdate },
          {
            data: addHistoryObj,
            list: InquiryHistoryList,
            op:
              existingInquiry && existingInquiry.StatusCode !== 'SAVED'
                ? BatchOperationUpdate
                : BatchOperationAdd,
          },
        ])
      }

      if (batchResult.result === 'PASS') {
        setRefresh(!refresh)
        setShowSaveSuccess(true)
        setTimeout(() => {
          setShowSaveSuccess(false)
        }, 1000)
        setShowTopSubmit(false)
        setShowBottomSubmit(false)

        if (existingInquiry && isFilesChanged()) {
          const deletedFiles = existingFiles.filter(
            (aItem) => !files.some((bItem) => bItem.fileName === aItem.fileName)
          )

          const listName = await mpfRListsHttp().getList(InquiryList)
          await Promise.all(
            deletedFiles.map((element) =>
              listName
                .blobs()
                .delete(
                  existingInquiry.eKey as string,
                  element.fileName,
                  element.folderName,
                  element.eTag
                )
            )
          )
        }

        const promises: any = []
        if (files?.length > 0) {
          promises.push(
            listAttachments(InquiryList, addedInquiry.eKey, files.slice(0, 10), '/Customer')
          )
        }
        await Promise.all(promises)
        if (action !== INQUIRY_STATUS_IDS.Saved) {
          await handleEmails(addedInquiry, userId, action)
          navigate(isCustomer ? `${ROUTE_TYPE}` : `${MY_INQUIRIES}`)
        } else {
          navigate(`${CREATE_INQUIRY}/${addedInquiry.InquiryGUID}`)
        }
      } else {
        deleteItem(InquiryList, { eKey: addedInquiry.eKey, eTag: addedInquiry.eTag })
        console.error('batchResult', batchResult)
      }
    } catch (error) {
      setShowPageOverlay(false)
      handleError(error, 'Error submitting inquiry')
    } finally {
      setShowPageOverlay(false)
      setLoading(false) // Reset loading state to re-enable button
    }
  }
  const getInvalidInputs = (validationObj: any) => {
    const keys = Object.keys(validationObj)

    const sortedInputs: any[] = Object.values(validationObj)
      .map((entry: any, index: number) => ({
        ...entry,
        name: keys[index],
      }))
      .sort((a: any, b: any) => a?.order - b?.order)

    return sortedInputs.filter((entry) => !entry.isValid)
  }
  const saveOrSubmitInquiry = async (
    action: '' | 'SAVED' | 'SUBMITTED' | 'UPDATED',
    position: 'bottom' | 'top',
    whenToCheckPII: 'first' | 'last' = 'last'
  ): Promise<void> => {
    setInquiryType(action)
    setShowPageOverlay(true)
    setActiveButton(false)
    const canSubmit = true
    let inputsAreValid = handleInputValidation(action)

    if (!inputsAreValid) {
      setShowPageOverlay(false)
    }
    const Inputs = userInputs
    delete Inputs.undefined
    const finalInput = {
      ...Inputs,
      Description: Inputs.Description?.getCurrentContent()?.getPlainText().trim(),
    }
    if (existingInquiry) {
      if (
        existingInquiryDetails.StatusCode !== STATUS_CODE.SAVED &&
        !isChangeDetected(existingInquiryDetails, finalInput) &&
        !isFilesChanged()
      ) {
        inputsAreValid = false
        setShowTopSubmit(false)
        setShowBottomSubmit(false)
        setShowNoChanges(true)
        setShowPageOverlay(false)
      }
    }
    const handlePIIValidationFirst = async (
      isValid: boolean,
      action: any,
      position: 'bottom' | 'top'
    ) => {
      const piiIsValid = await handlePIISubmission(action, position)
      if (isValid && canSubmit && piiIsValid) {
        await handleProfileValidations(action, position)
      } else {
        setIsValid(false)
        setShowTopSubmit(position === 'top')
        setShowBottomSubmit(position === 'bottom')
      }
      setShowPageOverlay(false)
    }
    const handlePIIValidationLast = async (
      isValid: boolean,
      action: any,
      position: 'bottom' | 'top'
    ) => {
      if (isValid && canSubmit) {
        const piiIsValid = await handlePIISubmission(action, position)
        if (piiIsValid) {
          await handleProfileValidations(action, position)
        } else {
          setIsValid(false)
          setShowPageOverlay(false)
          setShowTopSubmit(position === 'top')
          setShowBottomSubmit(position === 'bottom')
        }
      } else {
        setIsValid(false)
        setShowPageOverlay(false)
        setShowTopSubmit(position === 'top')
        setShowBottomSubmit(position === 'bottom')
      }
    }
    const handleProfileValidations = async (action: string, position: 'bottom' | 'top') => {
      const profileValidations = await userProfileChange()
      if (profileValidations.hasChanged) {
        setShowPageOverlay(false)
        setShowUpdatePopup(true)

        if (action === INQUIRY_STATUS_IDS.Submitted) {
          setShowTopSubmit(position === 'top')
          setShowBottomSubmit(position === 'bottom')
        }
      } else if (profileValidations.isNewProfile && action !== INQUIRY_STATUS_IDS.Saved) {
        setShowPageOverlay(false)
        handleCreateOrUpdateProfile('create')
      } else {
        submitInquiry(action)
        if (action === INQUIRY_STATUS_IDS.Submitted) {
          setShowTopSubmit(position === 'top')
          setShowBottomSubmit(position === 'bottom')
        }
      }
    }
    if (whenToCheckPII === 'first' && inputsAreValid) {
      await handlePIIValidationFirst(inputsAreValid, action, position)
    } else if (whenToCheckPII === 'last' && inputsAreValid ) {
      await handlePIIValidationLast(inputsAreValid, action, position)
    } else {
          setShowTopSubmit(position === 'top')
          setShowBottomSubmit(position === 'bottom')
    }
  }

  const handleEmails = async (data, userId, action) => {
    const navURL = sitePath + `/detailview/${data.InquiryGUID}`
    let bodyText = '' 
    let emailAction = ''

    switch (action) {
      case 'SUBMITTED':
        bodyText =  `Your inquiry has successfully been submitted.` 
        emailAction = 'SubmitInquiry'
      break
      case 'UPDATED':
        bodyText =  `Your inquiry as been successfully updated.` 
        emailAction = 'UpdateInquiry'
        break
    }
    const emailBodyData = {
      ...data,
      bodyText: bodyText,
      isInquiry: true,
    }

    const body = formatBody(emailAction, emailBodyData, navURL)
    const to = data.Customer.userID || data.Customer

    const emailObjData = {
      ...data,
      Action: emailAction,
      ItemGUID: data.InquiryGUID,
      ItemID: data.InquiryID,
      Title: data.Subject,
    }

    const emails: Promise<any>[][] = []
    const subjectAction = action === 'SUBMITTED' ? 'submitted' : 'updated'
    if (to) {
      emails.push(
        sendEmail({
          bcc: '',
          body: body,
          bodySurvey: '',
          cc: '',
          data: emailObjData,
          isDiscussion: false,
          isSurvey: false,
          loginuserId: userId,
          navURL: navURL,
          subject: ` Inquiry '${data.InquiryID}' has been ${subjectAction}`,
          to: to,
        })
      )
    }

    await Promise.all(emails)
  }

  const accordionItems = [
    {
      Id: 1,
      content: (
        <CustomerDetails
          changeInquiryType={changeInquiryType}
          currentUserObj={currentUserObj}
          dodPopulated={dodPopulated}
          dutyMailDisable={dutyMailDisable}
          initialUserInputs={initialUserInputs}
          inputValidationObj={inputValidationObj}
          inquiryForSelf={inquiryForSelf}
          isInquiryEditing={isInquiryEditing}
          pascodeChecks={pascodeChecks}
          pascodeMetadata={pascodeMetadata}
          selectedUserEmail={selectedUserEmail}
          selfForOther={selfForOther}
          setDodPopulated={setDodPopulated}
          setDutyMailDisable={setDutyMailDisable}
          setIsValid={setIsValid}
          setPascodeChecks={setPascodeChecks}
          setProfileLoaded={setProfileLoaded}
          setSelectedUserEmail={setSelectedUserEmail}
          setSelfForOther={setSelfForOther}
          setShowReadonlyInput={setShowReadonlyInput}
          setShowSubmit={setShowTopSubmit}
          setShowTopSubmit={setShowTopSubmit}
          setUniqueChange={setUniqueChange}
          setUserInputs={setUserInputs}
          showReadonlyInput={showReadonlyInput}
          userInputs={userInputs}
        />
      ),
      is_active: 'true',
      title: 'Customer Details',
    },
    {
      Id: 2,
      content: (
        <InquiryDetails
          categories={categories}
          fileError={fileError}
          files={files}
          filesProps={{ files, setFiles }}
          inputValidationObj={inputValidationObj}
          sections={sections}
          setFileError={setFileError}
          setFiles={setFiles}
          setUserInputs={setUserInputs}
          subcategories={subcategories}
          tooltipObj={tooltipObj}
          userInputs={userInputs}
        />
      ),
      is_active: 'false',
      title: 'Inquiry Details',
    },
  ]
  const rightAccordionItems = [
    {
      Id: 1,
      content: <CheckListCreateInquiry selectedChecklist={selectedChecklist} />,
      count: twoDigits(checklistCount),
      is_active: 'true',
      title: 'Checklist',
    },
    {
      Id: 2,
      content: (
        <RelatedKnowledgeBase
          filteredKnowledgeBase={filteredKnowledgeBase}
          setKnowledgeBaseCount={setKnowledgeBaseCount}
          userInputs={userInputs}
        />
      ),
      count: twoDigits(knowledgeBaseCount),
      is_active: 'false',
      title: 'Related Knowledge Base',
    },
  ]

  function popupCloseOpenFunctionality(type: any, val: any) {
    setActionBtnShowHide({
      ...actionBtnShowHide,
      close: false,
    })
    if (type) {
      setActionBtnShowHide({ ...actionBtnShowHide, [type]: true })
      buttonReferences(type)
    }
    if (val === 'top') {
      setShowTopSubmit(!showTopSubmit)
    } else {
      setShowBottomSubmit(!showBottomSubmit)
    }
  }
  function isFilesChanged(): boolean {
    const filesSet = new Set(files.map((file) => (file.fileName ? file.fileName : file.name)))
    const existingFilesSet = new Set(existingFiles.map((file) => file.fileName))
    const hasDifferences =
      existingFiles.some((aItem) => !filesSet.has(aItem.fileName)) ||
      files.some((aItem) => !existingFilesSet.has(aItem.fileName))
    return hasDifferences
  }
  function generateID(DOD: string, id: number): string {
    return DOD + '-' + id.toString().padStart(7, '0')
  }
  const handleOpen = (): void => {
    setShow(!show) // Toggle accordion
  }
  const onClose = (): void => {
    setShowCloseAlert(true)
  }
  const closeFunction = (button: string): void => {
    if (button === 'OK') {
      if (currentPage === '') navigate(`${ROUTE_TYPE}`)
      else {
        navigate(previousPage, { state: { prevPath: '/myinquiries-dueouts' } })
      }
    }
    setShowCloseAlert(false)
  }
  const noChangesAlert = (button: string) => {
    setShowNoChanges(false)
  }

  /**handlePIISubmission
   * Validates the PII (Personally Identifiable Information) for submission and handles the submission process.
   *
   * @param {string} action - The action being performed (e.g., 'SUBMITTED').
   * @param {string} position - The position to display PII compliance validation messages ('top' or 'bottom').
   * @returns {boolean} - Indicates whether the submission can proceed.
   */
  const handlePIISubmission = async (
    action: '' | 'SAVED' | 'SUBMITTED',
    position: 'bottom' | 'top'
  ) => {
    let canSubmit = true

    const piiScanData = {
      Description: userInputs.Description?.getCurrentContent()?.getPlainText().trim(), // Convert RTE (Rich Text Editor) content to plain text
      FileArray: Object.values(files), // Get an array of files
      Subject: userInputs.Subject, // Get the title from inquiry inputs
    }
    try {
      const uploadData = await getPIIScanInfo(piiScanData, piiScan)

      if (action.toUpperCase() === 'SUBMITTED') {
        setPIIScan({ ...uploadData.piiScanResult })
      }
      if (uploadData.canSubmit) {
        canSubmit = true
      } else {
        handlePIIValidationFailure(position)
        canSubmit = false
      }
    } catch (error) {
      console.error('PII validation failed:', error)
      canSubmit = false
    }
    return canSubmit
  }
  /**
   * Handles the display of PII compliance validation messages based on the specified position.
   *
   * @param {string} position - The position to display PII compliance validation messages ('top' or 'bottom').
   */
  const handlePIIValidationFailure = (position: 'bottom' | 'top'): void => {
    setShowTopSubmit(position === 'top')
    setShowBottomSubmit(position === 'bottom')
    setDisplayPII(true)
  }

  useEffect(() => {
    const filteredChecklistLookup = checklistsMetadataLookup.filter((item) => {
      return userInputs?.SubCategoryCode > 0
        ? item.CommonMetadata_Code === userInputs?.SubCategoryCode
        : item.CommonMetadata_Code === userInputs?.CategoryCode
    })

    const newChecklistItems = new Set(filteredChecklistLookup.map((i) => i.CheckListMetadata_Code))

    if (newChecklistItems) {
      const filteredChecklist: any = checklistsMetadata?.filter((item) => {
        return newChecklistItems.has(item.Code)
      })
      setSelectedChecklist(filteredChecklist)
    } else {
      setSelectedChecklist([])
    }
  }, [
    checklistsMetadataLookup,
    checklistsMetadata,
    userInputs.SubCategoryCode,
    userInputs.CategoryCode,
  ])

  useEffect(() => {
    // Filter knowledge base data by category
    const filteredKB: any[] = initialKnowledgeBase.filter((knowledgeBaseEntry: any) => {
      return (
        (knowledgeBaseEntry.CategoryCodes.includes(userInputs.CategoryCode) ||
          knowledgeBaseEntry.CategoryCodes.length === 0 ||
          userInputs.CategoryCode === -1) &&
        (knowledgeBaseEntry.SectionCodes.includes(userInputs.SectionCode) ||
          knowledgeBaseEntry.SectionCodes.length === 0 ||
          userInputs.SectionCode === -1)
      )
    })

    setFilteredKnowledgeBase(filteredKB)
    setKnowledgeBaseCount(filteredKB.length)
  }, [userInputs])

  useEffect(() => {
    setChecklistCount(selectedChecklist.length)
  }, [selectedChecklist])

  useEffect(() => {
    if (showBottomSubmit || showTopSubmit) {
      if (showBottomSubmit) {
        setTimeout(() => {
          validRef?.current?.scrollIntoView({ behavior: 'smooth' })
        }, 100)
      } else scrollToTop()
    }
  }, [showBottomSubmit, showTopSubmit])

  useEffect(() => {
    loadPII()
    isCustomer && !guid && setInquiryForSelf(true)
  }, [])
  useEffect(() => {
    setDisplayPII(false)
    if (Object.keys(piiScan).length > 0) {
      Object.keys(piiScan).forEach((key) => {
        if (key === 'FileArray') {
          for (const file of piiScan[key].value) {
            if (file?.resultsArray?.length > 0 && !file.userAnswer) {
              setDisplayPII(true)
            }
          }
        } else {
          if (piiScan[key]?.resultsArray?.length > 0 && !piiScan[key].userAnswer) {
            setDisplayPII(true)
          }
        }
      })
    }
  }, [piiScan])

  useEffect(() => {
    if (
      displayPII === false &&
      getInvalidInputs(selfForOther).length === 0 &&
      getInvalidInputs(inputValidationObj).length === 2
    ) {
      setShowTopSubmit(false)
      setShowBottomSubmit(false)
    }
  }, [displayPII])

  //update user inputs with existing inquiry when editing inquiry
  useEffect(() => {

    if (existingInquiry) {
      setShowPageOverlay(true)
      setInquiryForSelf(currentUserObj?.UserRID === existingInquiry.Customer?.userID)
      setIsInquiryEditing(true)
      setShowDiscardPopup(true)
      getFormBlobStorage()
      const existingInquiryCustomerDetails = {
        AssignedComponentCode: existingInquiry.AssignedComponentCode,
        Customer: existingInquiry.Customer,
        CustomerStatus: existingInquiry.CustomerStatus,
        DoDNumber: decodeHTML(existingInquiry.DoDNumber),
        DutyEmail: decodeHTML(existingInquiry.DutyEmail),
        DutyPhone: decodeHTML(existingInquiry.DutyPhone),
        Installation: decodeHTML(existingInquiry.Installation),
        ItemCreatedBy: existingInquiry.ItemCreatedBy,
        ItemModifiedBy: existingInquiry.ItemModifiedBy,
        OrgMAJCOM: existingInquiry.OrgMAJCOM,
        Organization: decodeHTML(existingInquiry.Organization),
        PASCODE: decodeHTML(existingInquiry.PASCODE),
        PASCODEServicedBy: existingInquiry.PASCODEServicedBy,
        PascodeID: existingInquiry.PascodeID,
        Rank: existingInquiry.Rank,
        ServicingMAJCOMCode: existingInquiry.ServicingMAJCOMCode,
        ServicingOrganization: existingInquiry.ServicingOrganization,
      }
      const existingInquiryDetails = {
        CategoryCode: existingInquiry.CategoryCode,
        Description: decodeHTML(existingInquiry.Description),
        SectionCode: existingInquiry.SectionCode,
        SubCategoryCode: existingInquiry.SubCategoryCode,
        Subject: existingInquiry.Subject,
        eKey: existingInquiry.eKey,
        eTag: existingInquiry.eTag,
      }
      setExistingInquiryDetails((prev) => ({
        ...prev,
        ...existingInquiryCustomerDetails,
        ...existingInquiryDetails,
      }))
      setUserInputs((prev) => ({
        ...prev,
        ...existingInquiryCustomerDetails,
        ...existingInquiryDetails,
      }))

      setPascodeChecks({
        HasLocation: true,
        HasOrganization: true,
        HasPASCODE: 'Yes',
      })
      setShowReadonlyInput(true)
      setTimeout(() => {
        setShowPageOverlay(false)
      }, 1000)
    }
  }, [existingInquiry, guid, currentUserObj, refresh])
  useEffect(() => {
    !guid && setIsInquiryEditing(false)
    if (currentUserObj && inquiryForSelf && !existingInquiry) {
      if (inquiryForSelf && currentUserObj.DutyEmail.trim() !== me.email.trim()) {
        currentUserObj.DutyEmail = me.email
        setUniqueChange('Duty Email has recently changed.')
      }
      setUserInputs(initialUserInputs) //reset user inputs after swithcing from edit to new inquiry
      setInquiryForSelf(true)
      setUserInputs((prevInputs) => ({
        ...prevInputs,
        AssignedComponentCode: currentUserObj.AssignedComponentCode,
        Customer: currentUserObj.UserRID,
        CustomerStatus: currentUserObj.ProfileStatus,
        DoDNumber: currentUserObj.DoDNumber,
        DutyEmail: currentUserObj.DutyEmail,
        DutyPhone: currentUserObj.DutyPhone,
        Installation: currentUserObj.Installation,
        ItemCreatedBy: currentUserObj.UserRID,
        ItemModifiedBy: currentUserObj.UserRID,
        OrgMAJCOM: currentUserObj.OrgMAJCOM,
        Organization: currentUserObj.Organization,
        PASCODE: currentUserObj.PASCODE,
        PASCODEServicedBy: currentUserObj.PASCODEServicedBy,
        PascodeID: currentUserObj.PascodeID,
        Rank: currentUserObj.Rank,
        ServicingMAJCOMCode: getMAJCOMCode(currentUserObj.ServicingMAJCOM, majcoms),
        ServicingOrganization: currentUserObj.ServicingOrganization,
      }))
      if (inquiryForSelf) setSelectedUserEmail(currentUserObj.DutyEmail)

      setPascodeChecks({
        HasLocation: true,
        HasOrganization: true,
        HasPASCODE: 'Yes',
      })
      setShowReadonlyInput(true)
    } else if (!guid) {
      setUserInputs(initialUserInputs)
    }
    setShowBottomSubmit(false)
    setShowTopSubmit(false)
    setPIIScan({})
  }, [currentUserObj, inquiryForSelf, existingInquiry])

  useEffect(() => {
    /**
     * Handles the retrieval of the Attachments from a blob storage and formats the Knowledge Base entries into a digestible object
     */
    async function getKnowledgeBaseData() {
      if (KnowledgeBaseData.length > 0) {
        await getBlobStorage(KnowledgeBaseList, KnowledgeBaseData).then((data) => {
          const formattedKnowledgeBase: any[] = data?.map((item, index) => ({
            ...item,
            Attachments: item.Attachments,
            eKey: item.eKey,
            extra: '',
            id: index,
            link: '/knowledgebase',
            postedDate: formatUTCDateToLocal(item.ItemCreated),
            showMore: false,
            subcontent: `Posted Date: ${formatUTCDateToLocal(item.ItemCreated)} | Section : ${
              item.Section
            } | Category : ${item.Category}`,
          }))
          setInitialKnowledgeBase(formattedKnowledgeBase)
        })
      }
    }
    getKnowledgeBaseData()
  }, [KnowledgeBaseData])
  const { state } = useLocation()
  useEffect(() => {
    // This effect runs whenever the location changes (when the user navigates to a different route)
    if (currentPage !== location.pathname) {
      // User has navigated to a new page, so update the previous page
      setPreviousPage(currentPage) // Store the previous page
      setCurrentPage(location.pathname) // Update the current page
    }
    if (state?.prevPath === PROFILE && previousPage === PROFILE) {
      setInquiryForSelf(true)
    }
  }, [location, currentPage]) // Runs whenever location or currentPage changes

  useEffect(() => {
    console.log(showTopSubmit)
  }, [showTopSubmit])

  return (
    <>
      <div id='scroll'></div>
      {displayAlert && (
        <Alert
          alerttextclass='poppins-semibold font-13 text-center'
          btn1className='btn btn btn-sm font-12 poppins-semibold btn-primary whitetext border-radius text-uppercase'
          btn1iconclassNmae='icon-checked font-12 pe-1'
          btn2classNmae='btn btn btn-border1 darktext btn-sm poppins-semibold font-12 border-radius text-uppercase'
          btn2iconclassNmae='icon-close color-primary font-12 pe-1'
          cancel='Cancel'
          className='alert-info'
          message={alertMessage}
          onClick={() => {
            setAlertMessage('')
            setDisplayAlert(false)
          }}
          yes='OK'
        />
      )}
      <PageHeader
        extras={
          <CreateActionBtns
            activeButton={activeButton}
            handleSaveOrSubmit={() => saveOrSubmitInquiry('SUBMITTED', 'top', WHEN_TO_CHECK_PII)}
            handleUpdate={() => saveOrSubmitInquiry('UPDATED', 'top', WHEN_TO_CHECK_PII)}
            isEditDueout={
              existingInquiry && existingInquiry.StatusCode !== INQUIRY_STATUS_IDS.Saved
                ? true
                : false
            }
            onClose={onClose}
            onSaveAsDraft={() => {
              onSaveAsDraft('top')
            }}
            onSubmit={(event) => {
              event?.preventDefault()
            }}
            showSubmit={showTopSubmit}
            type={'button'}
          />
        }
        icon='icon-create font-16 me-1 color-primary'
        id={'create-inquiry'}
        name='Create Inquiry'
        toolTip={
          <Buttons
            className='mx-2 btn-sm d-flex align-items-center whitetext bgcolor-22 btn-border-radius1 sourcesanspro font-13'
            label='Instructions'
            onClick={handleOpen}
            rIcon={show ? 'icon-arrow-down ms-1 font-12 rotate' : 'icon-arrow-down ms-1 font-12'}
            type='button'
          />
        }
      />
      <div className='container'>
        <div className=' mt-3'>
          <div>{show && <Instructions />}</div>
          <div>
            {showTopSubmit && !isValid && (
              <InquiryFormValidationMsgs
                displayPII={displayPII}
                inputValidationObj={inputValidationObj}
                piiScan={piiScan}
                popupCloseOpenFunctionality={popupCloseOpenFunctionality}
                ref={closeRef}
                selfForOther={selfForOther}
                setExpandDetailsAccordion={setExpandDetailsAccordion}
                setPIIScan={setPIIScan}
                setSelfForOther={setSelfForOther}
                updateClickPosition='top'
                validref={validRef}
              />
            )}
          </div>
          <div className='whitebg border-radius '>
            <div className='row '>
              <div className='col-sm-12 col-md-12 col-lg-9 mb-2 mt-0 pe-0 leftside'>
                <div className='p-2 pe-sm-2 pe-md-2 pe-0'>
                  <div className='formaccordions'>
                    <Accordions
                      accordionitemclass='border-radius'
                      className='accordions accordion-bordered'
                      expandedIndex={[1, 2]}
                      icon='icon-arrow-down font-13 ms-auto bold bgcolor-23 border-radius4 p-1'
                      inputProps={{
                        className: 'w-100 p-2',
                        contentclassName: 'border-top3 py-0',
                      }}
                      items={accordionItems}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-12 col-md-12 col-lg-3 mt-0 ps-sm-2 ps-md-2 rightside'>
                <div className='rightcontainer w-100 position-relative h-100'>
                  {initialKnowledgeBase.length > 0 || checklistCount > 0 ? (
                    <Accordions
                      accordionitemclass=''
                      className='accordions'
                      count='badge bgcolor-primary border-radius3 px-2 py-1 font-12 whitetext poppins-semibold ms-1'
                      icon='icon-arrow-down font-15 ms-auto bold pe-1'
                      inputProps={{
                        className: 'w-100 p-2 ',
                        contentclassName: 'py-0',
                      }}
                      isExpandAll={true}
                      items={rightAccordionItems}
                    />
                  ) : (
                    <div style={{ alignItems: 'center', display: 'flex', height: '100%' }}>
                      <p className='text-center py-2 subtitle-color2 montserratregular'>
                        Based on Category selection, the related Knowledge Base will populate. Based
                        on Subcategory selection, the required checklist will populate.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {showBottomSubmit && !isValid && (
            <div>
              <InquiryFormValidationMsgs
                displayPII={displayPII}
                inputValidationObj={inputValidationObj}
                piiScan={piiScan}
                popupCloseOpenFunctionality={popupCloseOpenFunctionality}
                selfForOther={selfForOther}
                setExpandDetailsAccordion={setExpandDetailsAccordion}
                setPIIScan={setPIIScan}
                setSelfForOther={setSelfForOther}
                updateClickPosition='bottom'
                validref={validRef}
              />
            </div>
          )}
        </div>
      </div>
      <div className='formheader d-flex px-3 py-1 whitebg shadow5 mt-3 bottom-buttons'>
        <div className='ms-auto formbuttons'>
          <CreateActionBtns
            activeButton={activeButton}
            handleSaveOrSubmit={() => saveOrSubmitInquiry('SUBMITTED', 'bottom', WHEN_TO_CHECK_PII)}
            handleUpdate={() => saveOrSubmitInquiry('UPDATED', 'bottom', WHEN_TO_CHECK_PII)}
            isEditDueout={
              existingInquiry && existingInquiry.StatusCode !== INQUIRY_STATUS_IDS.Saved
                ? true
                : false
            }
            onClose={onClose}
            onSaveAsDraft={() => {
              onSaveAsDraft('bottom')
            }}
            onSubmit={(event) => {
              event?.preventDefault()
            }}
            showSubmit={showBottomSubmit}
            type={'button'}
          />
        </div>
      </div>
      {(showPageOverlay || loading || inquiryIsLoading) && <PageOverlayBusy />}
      {showNoChanges && (
        <Alert
          alerttextclass='poppins-semibold font-13 text-center'
           btn1className='btn btn btn-sm font-12 poppins-semibold btn-primary whitetext border-radius text-uppercase'
          btn1iconclassNmae='icon-checked font-12 pe-1'
          btn2classNmae='btn btn btn-border1 darktext btn-sm poppins-semibold font-12 border-radius text-uppercase'
          btn2iconclassNmae='icon-close color-primary font-12 pe-1'
          cancel='Cancel'
          className='alert-info'
          message={'No Changes Detected'}
          onClick={noChangesAlert}
          yes='OK'
        />
      )}
      {showUpdatePopup && (
        <Alert
          alerttextclass='poppins-semibold font-13 text-center'
          btn1className='btn btn btn-sm font-12 poppins-semibold btn-primary whitetext border-radius text-uppercase'
          btn1iconclassNmae='icon-checked font-12 pe-1'
          btn2classNmae='btn btn btn-border1 darktext btn-sm poppins-semibold font-12 border-radius text-uppercase'
          btn2iconclassNmae='icon-close color-primary font-12 pe-1'
          cancel='No'
          className='alert-info'
          handleRouteNo={() => submitInquiry(inquiryType)}
          handleRouteYes={() => handleCreateOrUpdateProfile('update', inquiryType)}
          message={`${uniqueChange} Do you want to update profile details?`}
          setShowAlert={setShowUpdatePopup}
          yes='Yes'
        />
      )}
      {showCloseAlert && (
        <Alert
          alerttextclass='poppins-semibold font-13 text-center'
          btn1className='btn btn btn-sm font-12 poppins-semibold btn-primary whitetext border-radius text-uppercase'
          btn1iconclassNmae='icon-checked font-12 pe-1'
          btn2classNmae='btn btn btn-border1 darktext btn-sm poppins-semibold font-12 border-radius text-uppercase'
          btn2iconclassNmae='icon-close color-primary font-12 pe-1'
          cancel='Cancel'
          className='alert-info'
          message={'Are you sure you want to close this page?'}
          onClick={closeFunction}
          yes='OK'
        />
      )}
      {showCreateNewPopup && (
        <Alert
          alerttextclass='poppins-semibold font-13 text-center'
          btn1className='btn btn btn-sm font-12 poppins-semibold btn-primary whitetext border-radius text-uppercase'
          btn1iconclassNmae='icon-checked font-12 pe-1'
          btn2classNmae='btn btn btn-border1 darktext btn-sm poppins-semibold font-12 border-radius text-uppercase'
          btn2iconclassNmae='icon-close color-primary font-12 pe-1'
          cancel='Cancel'
          className='alert-info'
          handleRouteNo={() => submitInquiry(inquiryType)}
          handleRouteYes={() => handleCreateOrUpdateProfile('create')}
          message={'Do you want to create a new profile with the customer details?'}
          setShowAlert={setShowCreateNewPopup}
          yes='OK'
        />
      )}
      {showSaveSuccess && (
        <SuccessMessage
          submissionType='Inquiry Form'
          successType={
            inquiryType === INQUIRY_STATUS_IDS.Saved
              ? 'Saved'
              : inquiryType === 'UPDATED'
              ? 'Updated'
              : 'Submitted'
          }
        />
      )}
    </>
  )
}
export default forwardRef(InquiryForm)
